import React from 'react';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import { Grid, Typography } from '@material-ui/core';
import { Link } from 'react-router-dom';
import UserRoleSpecificContent from 'modules/shell/components/UserRoleSpecificContent';
import { GiftIcon } from 'sharedComponents/Icons';
import { Drawer, Image, GiftOptions, GiftOptionsButton } from 'sharedComponents';
import { ProductPropTypes } from 'utils/PropTypesValues';
import { NO_IMAGE_AVAILABLE } from 'constants/index';
import { isUrlValid, formatNumberToUSD } from 'utils';
import { PriceAdjust, PriceAdjustButton, ProductDetails, ProductLinkButton } from './components';
import PinBox from '../PinBox';

const wrapIntoLink = (children, link) => {
  if (link) {
    return <Link to={link}>{children}</Link>;
  }

  return children;
};

const Product = ({
  classes,
  clientId,
  product,
  onClose,
  onChangeQuantity,
  onMoveItemClick,
  isOrderWrappable,
  isItemDisabled,
  viewOnly,
  ordered,
  bagType,
  inStoreOrder,
  cmosOrder,
  userBrand,
  bopsOrderFlag,
  giftWrapCode,
  mainBrand,
  onChangeDeliveryDate,
  confirmationView,
  saveScrollPosition,
  orderData,
  isConciergeCart,
}) => {
  if (_isEmpty(product)) return null;

  const {
    name,
    brand,
    price = {},
    color: productColor,
    size: productSize,
    imageUrl,
    thumbnailUrl,
    quantity = 0,
    inventory = 0,
    itemskuId,
    giftOptions,
    isGiftWrappable,
    isBackorder,
    isDropship,
    extendedPrice,
    isNegative,
    webProductSku = '',
    isIce,
    isPerishable,
    assCommissionPins,
    offerItemId,
  } = product;

  const [drawer, setDrawer] = React.useState(null);
  const [webProductId] = webProductSku !== null ? webProductSku.split('/') : [''];

  const imgURL = React.useMemo(() => {
    if (isUrlValid(imageUrl)) {
      return imageUrl;
    }
    if (isUrlValid(thumbnailUrl)) {
      return thumbnailUrl;
    }
    return NO_IMAGE_AVAILABLE[userBrand];
  }, [imageUrl, thumbnailUrl]);

  const isJeweleryProduct = offerItemId?.includes('_P') || offerItemId?.includes('_J');
  const isOutOfStock = quantity > inventory && !isBackorder && !isPerishable;
  const itemPrice =
    price.adjustedPromoPrice ||
    price.adjustedPrice ||
    price.promoPrice ||
    price.retailPrice ||
    price.orderPrice ||
    price.currentPrice ||
    '-';
  const hidePriceAdjust = viewOnly || isOutOfStock || isJeweleryProduct;
  const hideGiftOptions = !isGiftWrappable || viewOnly || isOutOfStock;
  const hideRemoveButton = bagType === 'online' ? true : viewOnly && !onClose;
  const hideQuantityControls = bagType === 'online' || (viewOnly && !onChangeQuantity);
  const showErrorMessage = isOutOfStock && !inventory && !ordered && !isBackorder;
  const showFromOnlineToConciergeButton = bagType === 'online' && !isItemDisabled && (!isOutOfStock || isDropship);
  const showFromSflToConciergeButton = bagType === 'sfl' && !isOutOfStock;

  const onCloseBtnClickHandler = () => onClose && onClose(product);

  const onDrawerClose = () => setDrawer(null);
  const promoPrice = price.promoPrice ? price.promoPrice : null;

  const openPriceAdjustment = () =>
    setDrawer({
      title: 'Price Adjust',
      content: (
        <PriceAdjust
          clientId={clientId}
          productName={name}
          productId={itemskuId}
          productPrice={price.currentPrice}
          onClose={onDrawerClose}
          promoPrice={promoPrice}
          isConciergeCart={isConciergeCart}
        />
      ),
    });

  const openGiftOptions = () => {
    setDrawer({
      title: 'Gift Options',
      content: (
        <GiftOptions
          clientId={clientId}
          itemskuId={itemskuId}
          itemGiftOptions={giftOptions}
          onClose={onDrawerClose}
          isConciergeBag
        />
      ),
    });
  };

  let formattedPrice = '';
  const multiplier = isNegative ? -1 : 1;

  if (itemPrice && !inStoreOrder && !cmosOrder) {
    formattedPrice = formatNumberToUSD(Number(itemPrice) * multiplier);
  } else if (extendedPrice) {
    formattedPrice = formatNumberToUSD(Number(extendedPrice) * multiplier);
  }

  // prepare link to the corresponding PDP in case of web product id is provided
  // Also pass color and size so PDP could make these parameters are selected by default
  let productLink;

  if (webProductId) {
    productLink = {
      pathname: `/products/details/${webProductId}`,
      state: {
        selectedColor: productColor,
        selectedSize: productSize,
      },
    };
  }

  const renderHasGiftWrapText = () => {
    return (
      <div className={classes.giftWrapText}>
        <GiftIcon className={classes.giftWrapIcon} />
        <span>Gift Wrap</span>
      </div>
    );
  };

  return (
    <div className={classes.container} data-testid="product">
      {showErrorMessage && <div className={classes.errorMessage}>This item is currently unavailable</div>}
      <div className={classes.productWrapper}>
        {wrapIntoLink(
          <div className={classes.productImg}>
            <Image src={imgURL} alt={`${brand} ${name}`} data-testid="product-image" userBrand={userBrand} />
          </div>,
          productLink,
        )}
        <ProductDetails
          product={product}
          ordered={ordered}
          inStoreOrder={inStoreOrder}
          onChangeQuantity={onChangeQuantity}
          onChangeDeliveryDate={onChangeDeliveryDate}
          hideQuantityControls={hideQuantityControls}
          productLink={productLink}
          bopsOrderFlag={bopsOrderFlag}
          isCheckoutPage={viewOnly}
          hideDeliveryDateSelector={showFromSflToConciergeButton}
          confirmationView={confirmationView}
          userBrand={userBrand}
          orderId={orderData?.cmosOrderNumber || orderData?.orderId}
        />
      </div>
      <UserRoleSpecificContent roles={['store_manager', 'store_general_manager', 'stylist_manager']}>
        {assCommissionPins?.length > 0 ? (
          <PinBox
            boxName="Associate PINs"
            pins={assCommissionPins}
            withMargin
            additionalFnOnClick={saveScrollPosition}
            dataForCache={orderData}
          />
        ) : null}
      </UserRoleSpecificContent>
      <Grid>
        <GiftOptionsButton
          hideGiftOptions={hideGiftOptions}
          isOrderWrappable={isOrderWrappable}
          isProductWrappable={product.isGiftWrappable}
          giftOptions={giftOptions}
          onClick={openGiftOptions}
          mainBrand={mainBrand}
        />
      </Grid>
      <Grid container justifyContent="space-between" wrap="nowrap">
        <Grid item container direction="column">
          <Grid>
            <ProductLinkButton
              viewOnly={viewOnly}
              onClick={onMoveItemClick}
              data-testid="save-for-later"
              buttonText="Save for later"
            />
          </Grid>
          <Grid>
            {showFromSflToConciergeButton && (
              <ProductLinkButton
                onClick={onMoveItemClick}
                data-testid="move-from-sfl-to-concierge"
                buttonText="Move to Bag"
              />
            )}
          </Grid>
          <Grid>
            {showFromOnlineToConciergeButton && (
              <ProductLinkButton
                onClick={onMoveItemClick}
                data-testid="product-move-to-concierge"
                buttonText="Add to Concierge Bag"
              />
            )}
          </Grid>
          <Grid>
            {!hideRemoveButton && (
              <ProductLinkButton
                onClick={onCloseBtnClickHandler}
                data-testid="product-remove-btn"
                buttonText="Remove"
              />
            )}
          </Grid>
          {giftWrapCode && <Grid> {renderHasGiftWrapText()}</Grid>}
        </Grid>
        <Grid item container justifyContent="flex-end">
          {((!!itemPrice && !inStoreOrder) || (!!extendedPrice && inStoreOrder)) && (
            <div className={classes.priceContainer}>
              {!isIce && (
                <PriceAdjustButton hidePriceAdjust={hidePriceAdjust} onClick={openPriceAdjustment} price={price} />
              )}
              <Typography
                className={classes.price}
                color={isNegative ? 'error' : 'textPrimary'}
                data-testid="item-price"
              >
                {formattedPrice}
              </Typography>
            </div>
          )}
        </Grid>
      </Grid>
      <Drawer title={drawer ? drawer.title : ''} open={!!drawer} onClose={onDrawerClose} data-testid="drawer">
        {drawer && drawer.content}
      </Drawer>
    </div>
  );
};

Product.propTypes = {
  product: ProductPropTypes,
  classes: PropTypes.objectOf(PropTypes.string),
  viewOnly: PropTypes.bool,
  ordered: PropTypes.bool,
  onClose: PropTypes.func,
  onChangeQuantity: PropTypes.func,
  onChangeDeliveryDate: PropTypes.func,
  onMoveItemClick: PropTypes.func,
  isItemDisabled: PropTypes.bool,
  bagType: PropTypes.string,
  inStoreOrder: PropTypes.bool,
  cmosOrder: PropTypes.bool,
  isConciergeCart: PropTypes.bool,
};

Product.defaultProps = {
  product: {},
  classes: {},
  viewOnly: false,
  ordered: false,
  onMoveItemClick: () => {},
  inStoreOrder: false,
  cmosOrder: false,
  isConciergeCart: false,
};

export default Product;
